import classNames from "classnames/bind";
import { isPlainObject } from "es-toolkit/compat";
import React, { forwardRef, useMemo, useRef } from "react";
import {
  useController,
  type FieldValues,
  type ValidateResult,
} from "react-hook-form";
import { InputWithLabels } from "../_components/InputWithLabels";
import TextAreaInputBare from "../TextAreaInput/TextAreaInput";
import { mergeProps } from "../../utils/component-props";
import { FileInputBare } from "../FileInput";
import styles from "./TextWithFileInput.module.scss";
import type {
  ControlledTextWithFileInputProps,
  TextWithFileInputProps,
  TextWithFileInputValueType,
} from "./types";
import { nanoid } from "nanoid";

const cx = classNames.bind(styles);

/** @deprecated will be removed once Ops Hub is deprecated. these inputs should really be separate. */
const TextWithFileInput = forwardRef<
  HTMLTextAreaElement,
  TextWithFileInputProps
>(
  (
    {
      onChange,
      onBlur,
      value,
      isDisabled,
      showIcon,
      error,
      "aria-invalid": ariaInvalid,
      name,
      multiple = false,
      textAreaPlaceholder = "Type your note here",
      fileInputPlaceholder = "Drag files here",
      isInline = false,
      isFluid = true,
      flairSlot,
    }: TextWithFileInputProps,
    ref
  ) => {
    const fileInputId = useRef(nanoid(6));
    const onTextChange = (text: string) => {
      onChange?.({
        text,
        files: value?.files,
      });
    };

    const onFileChange = (files: TextWithFileInputValueType["files"]) => {
      onChange?.({
        text: value?.text,
        files,
      });
    };

    return (
      <fieldset
        className={cx("container", {
          "container--inline": isInline,
          "container--fluid": isFluid,
        })}
        disabled={isDisabled}
      >
        {/* TODO: add legend here */}

        <TextAreaInputBare
          ref={ref}
          name={name}
          placeholder={textAreaPlaceholder}
          value={value?.text ?? ""}
          onChange={onTextChange}
          onBlur={onBlur}
          isDisabled={isDisabled}
          error={error}
          aria-invalid={ariaInvalid}
          showIcon={showIcon}
          isInline={isInline}
          flairSlot={flairSlot}
        />

        <FileInputBare
          id={fileInputId.current}
          // a unique name so its labels' `htmlFor` link to FileInput, not TextAreaInput
          name={`${name}-fileInput`}
          placeholder={fileInputPlaceholder}
          {...(value?.files ? { value: value.files } : {})}
          onChange={onFileChange}
          allowMultiple={multiple}
          onBlur={onBlur}
          isDisabled={isDisabled}
          error={error}
          isFluid
          isInline={false}
        />
      </fieldset>
    );
  }
);

TextWithFileInput.displayName = "TextWithFileInput";

export const ControlledTextWithFileInput = <T extends FieldValues>({
  name,
  defaultValue,
  rules,
  label,
  isLabelShown,
  labelPosition,
  description,
  isRequired,
  isOptionalTextShown,
  shouldShowDescriptionInTooltip,
  isInline = false,
  isFluid = true,
  ...restProps
}: ControlledTextWithFileInputProps<T>) => {
  const { isDisabled, isLoading, isBusy } = restProps;
  const { field, fieldState } = useController({
    name,
    defaultValue,
    rules: {
      ...rules,
      validate:
        rules?.validate && typeof rules.validate === "function"
          ? rules.validate
          : {
              ...(isRequired
                ? {
                    isRequired: (
                      value: TextWithFileInputValueType
                    ): ValidateResult =>
                      [null, undefined, ""].includes(value?.text)
                        ? `${typeof label === "string" ? label : "Text with file input"} is required`
                        : undefined,
                  }
                : {}),
              ...(rules?.validate && isPlainObject(rules.validate)
                ? rules.validate
                : {}),
            },
    },
  });

  const mergedProps = useMemo(() => {
    return mergeProps(restProps, {
      onChange: field.onChange,
      onBlur: field.onBlur,
      disabled: field.disabled,
      name: field.name,
      ref: field.ref,
    });
  }, [
    restProps,
    field.onChange,
    field.onBlur,
    field.disabled,
    field.name,
    field.ref,
  ]);

  const invalidMessage = fieldState.invalid
    ? fieldState.error?.message ||
      `Input is invalid (${fieldState.error?.type})`
    : undefined;

  return (
    <InputWithLabels
      colorScheme="light"
      isDisabled={isDisabled ?? false}
      isLoading={isLoading ?? false}
      isBusy={isBusy ?? false}
      label={label}
      isLabelShown={isLabelShown}
      labelPosition={labelPosition}
      description={description}
      isRequired={isRequired}
      isOptionalTextShown={isOptionalTextShown}
      shouldShowDescriptionInTooltip={shouldShowDescriptionInTooltip}
      isInline={isInline}
      isFluid={isFluid}
      invalidMessage={invalidMessage}
    >
      {({ invalidMessageId, labelId, inputId, flairSlot }) => (
        <TextWithFileInput
          id={inputId}
          labelledById={labelId}
          error={fieldState.error}
          invalidMessage={invalidMessage}
          aria-invalid={fieldState.invalid}
          aria-describedby={invalidMessageId}
          {...(!isLabelShown && !description && typeof label === "string"
            ? { "aria-label": label }
            : {})}
          isInline={isInline}
          isFluid={isFluid}
          flairSlot={flairSlot}
          {...mergedProps}
          value={field.value}
        />
      )}
    </InputWithLabels>
  );
};

ControlledTextWithFileInput.displayName = "ControlledTextWithFileInput";

export default TextWithFileInput;
